import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API_URL from '../config';

const getAuthHeader = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Fetch jobs with filters
export function useJobs(filters = {}) {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'All') {
      params.append(key, value);
    }
  });

  return useQuery({
    queryKey: ['email-jobs', filters],
    queryFn: async () => {
      const url = `${API_URL}/api/email-jobs?${params.toString()}`;
      const response = await fetch(url, {
        headers: getAuthHeader(),
      });
      if (!response.ok) throw new Error('Failed to fetch jobs');
      return response.json();
    },
  });
}

// Fetch job stats
export function useJobStats() {
  return useQuery({
    queryKey: ['email-job-stats'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/email-jobs/stats`, {
        headers: getAuthHeader(),
      });
      if (!response.ok) throw new Error('Failed to fetch job stats');
      return response.json();
    },
    refetchInterval: 30000, // Poll every 30 seconds
  });
}

// Fetch single job
export function useJob(id) {
  return useQuery({
    queryKey: ['email-job', id],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/email-jobs/${id}`, {
        headers: getAuthHeader(),
      });
      if (!response.ok) throw new Error('Failed to fetch job');
      return response.json();
    },
    enabled: !!id,
  });
}

// Cancel job
export function useCancelJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId) => {
      const response = await fetch(`${API_URL}/api/email-jobs/${jobId}/cancel`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel job');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['email-job-stats'] });
    },
  });
}

// Requeue job
export function useRequeueJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId) => {
      const response = await fetch(`${API_URL}/api/email-jobs/${jobId}/requeue`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to requeue job');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['email-job-stats'] });
    },
  });
}

// Bulk cancel jobs
export function useBulkCancelJobs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params) => {
      const response = await fetch(`${API_URL}/api/email-jobs/bulk-cancel`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });
      if (!response.ok) throw new Error('Failed to cancel jobs');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['email-job-stats'] });
    },
  });
}

// Bulk requeue jobs
export function useBulkRequeueJobs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobIds) => {
      const response = await fetch(`${API_URL}/api/email-jobs/bulk-requeue`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ jobIds }),
      });
      if (!response.ok) throw new Error('Failed to requeue jobs');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['email-job-stats'] });
    },
  });
}

// Fetch system status
export function useSystemStatus() {
  return useQuery({
    queryKey: ['system-status'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/system/status`, {
        headers: getAuthHeader(),
      });
      if (!response.ok) throw new Error('Failed to fetch system status');
      return response.json();
    },
    refetchInterval: 30000, // Poll every 30 seconds
  });
}

// Pause sending
export function usePauseSending() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/api/system/pause-sending`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      if (!response.ok) throw new Error('Failed to pause sending');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-status'] });
    },
  });
}

// Resume sending
export function useResumeSending() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/api/system/resume-sending`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      if (!response.ok) throw new Error('Failed to resume sending');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-status'] });
    },
  });
}
